import * as React from 'react';

/**
 * A press that leaves for Invent's IdP — a sign-in, or its end session. While the address is
 * being built (the IdP's discovery document read), the press is `leaving` and its button
 * disabled; an address that cannot be built keeps the page here, `unreachable`, for another
 * press.
 *
 * Back from the IdP's page, the browser may restore this one as it was left, still leaving: a
 * restored page is a page to press again.
 */
export function useIdpDeparture() {
  const [leaving, setLeaving] = React.useState(false);
  const [unreachable, setUnreachable] = React.useState(false);

  React.useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) setLeaving(false);
    };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, []);

  const leave = React.useCallback(async (go: () => Promise<void>) => {
    setLeaving(true);
    setUnreachable(false);
    try {
      await go();
    } catch (idpError) {
      console.warn('[Auth] Could not leave for the IdP:', idpError);
      setUnreachable(true);
      setLeaving(false);
    }
  }, []);

  return { leaving, unreachable, leave };
}

/** What a press that could not leave says, above its button. */
export function IdpUnreachable() {
  return (
    <p role="alert" className="text-sm font-medium text-destructive">
      Could not reach Invent’s sign-in. Try again.
    </p>
  );
}
